import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { APP_NAME, SUPPORT_EMAIL } from "@/lib/constants";
import { useSystemSettings } from "@/hooks/use-system-settings";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AccessibilityStatement = ({ open, onOpenChange }: Props) => {
  const { data: settings } = useSystemSettings();
  const customContent = settings?.accessibility_statement_content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            הצהרת נגישות - {APP_NAME}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 text-slate-700 leading-relaxed text-sm">
          {customContent ? (
             <MarkdownRenderer content={customContent} allowStyle={true} />
          ) : (
            <>
              <p className="text-slate-500 font-medium">תאריך עדכון אחרון: פברואר 2026</p>
              
              <p>
                אנו ב-{APP_NAME} רואים חשיבות עליונה בהנגשת האתר והמערכת שלנו לאנשים עם מוגבלויות, מתוך אמונה עמוקה כי לכל אדם מגיעה הזכות לשוויון, כבוד, נוחות ועצמאות במרחב הדיגיטלי.
              </p>

              <h3 className="text-lg font-bold text-slate-900 mt-6 border-b border-slate-100 pb-2">רמת הנגישות</h3>
              <p>
                אתר זה עומד בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג 2013.
                התאמות הנגישות בוצעו עפ"י המלצות התקן הישראלי (ת"י 5568) לנגישות תכנים באינטרנט ברמת AA ומסמך ה-WCAG 2.0 הבינלאומי.
              </p>

              <h3 className="text-lg font-bold text-slate-900 mt-6 border-b border-slate-100 pb-2">התאמות נגישות שבוצעו באתר</h3>
              <ul className="list-disc pl-4 pr-6 space-y-2">
                <li><strong>תפריט נגישות ייעודי:</strong> הוספנו כפתור נגישות צף המאפשר התאמת האתר אישית (הגדלת גופן, ניגודיות גבוהה, עצירת אנימציות, מסכת קריאה ועוד).</li>
                <li><strong>תמיכה במקלדת:</strong> האתר מותאם לגלישה באמצעות מקלדת (שימוש במקשי Tab, Enter, ו-Esc).</li>
                <li><strong>התאמה לקוראי מסך:</strong> שימוש בתגיות ARIA מלאות לשיפור חווית הגלישה לעיוורים ולקויי ראייה.</li>
                <li><strong>עיצוב וניגודיות:</strong> הקפדה על יחס ניגודיות תקין וברור בין צבעי הטקסט לרקע, בכל מרכיבי האתר.</li>
                <li><strong>רספונסיביות:</strong> האתר מותאם לשימוש בטלפונים חכמים וטאבלטים בצורה מלאה.</li>
              </ul>

              <h3 className="text-lg font-bold text-slate-900 mt-6 border-b border-slate-100 pb-2">יצירת קשר ומשוב</h3>
              <p>
                אנו ממשיכים במאמצים לשפר את נגישות האתר כחלק ממחויבותנו לאפשר שימוש בו עבור כלל האוכלוסייה.
                אם נתקלתם בבעיית נגישות או שיש לכם הצעה לשיפור, נשמח לשמוע מכם כדי שנוכל לתקן זאת בהקדם:
              </p>
              <ul className="list-none space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                <li><strong>שם רכז/ת נגישות:</strong> צוות הנגישות, {APP_NAME}</li>
                <li><strong>דוא"ל:</strong> <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 hover:underline font-medium">{SUPPORT_EMAIL}</a></li>
              </ul>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};